/*eslint no-useless-escape: "off"*/

import { FlexSpinner, onFormatDate } from 'components'
import { useDeleteBlobMutation } from 'components/DocumentGenerator/documentGenerator.generated'
import { DownloadLink } from 'components/DownloadLink'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { FormikMailAddressPicker } from 'components/PersonPicker'
import { FieldProps } from 'formik'
import { mapFieldToTextField } from 'formik-office-ui-fabric-react'
import { AuthorizedField as Field } from 'forms/AuthorizedField'
import { FieldStackItem } from 'forms/FormStack'
import { omitDeep } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { uniqueId } from 'lodash'
import {
  Blob,
  FlightType,
  Mail,
  MailFolder,
  MailTemplateRendered,
  PersonType,
  Proving,
  ProvingChange,
} from 'model'
import {
  Checkbox,
  IStackTokens,
  ITag,
  Icon,
  IconButton,
  Label,
  PanelType,
  Stack,
  TagPicker,
  Text,
  TextField,
} from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isArray } from 'util'
import { getParticipantTypeDisplayName } from 'views/helpers'
import * as Yup from 'yup'
import { TextEditor } from '../TextEditor'
import {
  useCreateMailMutation,
  useGenerateMailFromTemplateMutation,
  useLoadMailLazyQuery,
  useLoadMailProvingPickerQuery,
  useLoadMailsWithBodyLazyQuery,
  useUpdateMailMutation,
} from './Mailbox.generated'

export enum MailboxFormMode {
  New,
  Edit,
  EditDraft,
  Reply,
}

const emptyMail = {
  name: '',
  to: [],
  cc: [],
  subject: '',
  body: '',
  provingTag: null,
}

export interface MaiboxFormProps {
  mode: MailboxFormMode
  mailId?: string
  isOpen: boolean
  proving?: Proving
  templateId?: string
  initialMail?: Pick<MailTemplateRendered, 'to' | 'cc' | 'subject' | 'body'>
  context?: any
  canSaveAsDraft?: boolean
  location?: string
  changes?: ProvingChange[]
  loadNewMails?: boolean
  onDismiss: () => void
  onSaved: () => void
}

export class MailFormValidationSchema {
  constructor() {
    return Yup.object().shape({
      subject: Yup.string().required(),
      draftEnabled: Yup.boolean(),
      to: Yup.array().when('draftEnabled', {
        is: true,
        then: Yup.array().notRequired(),
        otherwise: Yup.array().required().min(1),
      }),
    })
  }
}

const themedMediumStackTokens: IStackTokens = {
  childrenGap: 'm',
  padding: 'm',
}

export const MailboxForm: React.FunctionComponent<MaiboxFormProps> = ({
  mode,
  isOpen,
  mailId,
  onDismiss,
  onSaved,
  proving,
  templateId,
  initialMail,
  location,
  canSaveAsDraft = true,
  changes = [],
  context,
  loadNewMails = true,
}) => {
  const { t } = useTranslation()
  const auth = useAuthContext()
  const [initialValues, setinitialValues] = useState()
  const [isDraft, setIsDraft] = useState(mode === MailboxFormMode.EditDraft)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [deleteBlob] = useDeleteBlobMutation()
  const [createMail] = useCreateMailMutation()
  const [updateMail] = useUpdateMailMutation()
  const [
    loadMails,
    { loading: loadingMails, data: mailsData, refetch: refetchMail },
  ] = useLoadMailsWithBodyLazyQuery()
  const [loadMail, { data: mailData }] = useLoadMailLazyQuery()

  const [generateMailFromTemplate] = useGenerateMailFromTemplateMutation()
  const { data } = useLoadMailProvingPickerQuery()
  // const [currentProving] = useCurrentProving()
  // const {
  //   data: mailChangesData,
  //   loading: mailChangesLoading,
  // } = useMailChangesQuery({
  //   variables: {
  //     provingId: currentProving._id,
  //     participants: context ? context.map((i: any) => i._id) : [],
  //     paths: ['.*'],
  //   },
  // })

  // const changes =
  //   mailChangesData && mailChangesData.mailChanges
  //     ? mailChangesData.mailChanges
  //     : []

  const provings = data && data.provings ? data.provings : null
  const replaceVariables = useCallback(
    (contentToReplace: any): any => {
      let result = contentToReplace
      // Erprobungsnummer
      result = result.replace(
        /\[ErprobungsNr\]/g,
        proving && proving.id ? proving.id : ''
      )
      // Erprobungstitel
      result = result.replace(
        /\[Erprobung\]/g,
        proving && proving.title ? proving.title : ''
      )

      // Erprobungsdatum
      result = result.replace(
        /\[ErprobungDatumVon\]/g,
        proving && proving.dateFrom
          ? onFormatDate(new Date(proving.dateFrom))
          : ''
      )
      result = result.replace(
        /\[ErprobungDatumBis\]/g,
        proving && proving.dateTo ? onFormatDate(new Date(proving.dateTo)) : ''
      )

      // Antragsteller
      result = result.replace(
        /\[Antragsteller\]/g,
        proving && proving.createdBy ? proving.createdBy.name : ''
      )
      result = result.replace(
        /\[AntragstellerMail\]/g,
        proving && proving.createdBy ? proving.createdBy.email : ''
      )
      result = result.replace(
        /\[NachnameAntragsteller\]/g,
        proving && proving.createdBy ? proving.createdBy.lastName : ''
      )

      // Verantwortlicher
      result = result.replace(
        /\[Verantwortlicher\]/g,
        proving && proving.responsible ? proving.responsible[0].name : ''
      )
      result = result.replace(
        /\[NachnameVerantwortlicher\]/g,
        proving && proving.responsible ? proving.responsible[0].lastName : ''
      )
      result = result.replace(
        /\[VerantwortlicherMail\]/g,
        proving && proving.responsible ? proving.responsible[0].email : ''
      )

      // Stellvertreter
      result = result.replace(
        /\[NachnameStellvertreter\]/g,
        proving &&
          proving.deputy &&
          proving.deputy[0] &&
          proving.deputy[0].lastName
          ? proving.deputy[0].lastName
          : ''
      )
      result = result.replace(
        /\[StellvertreterMail\]/g,
        proving &&
          proving.deputy &&
          proving.deputy[0] &&
          proving.deputy[0].email
          ? proving.deputy[0].email
          : ''
      )

      // Standort
      result = result.replace(
        /\[Standort\]/g,
        proving && proving.provingGround ? proving.provingGround.title : ''
      )
      result = result.replace(
        /\[StandortKürzel\]/g,
        proving && proving.provingGround ? proving.provingGround.abbr : ''
      )
      result = result.replace(
        /\[Land\]/g,
        proving && proving.provingGround
          ? proving.provingGround.countryCode
          : ''
      )

      // Hotelname
      result = result.replace(
        /\[Hotelname\]/g,
        context?.[0]?.accomodation?.accomodation?.title || ''
      )

      const trackHotels = context?.[0]?.trackHotels
      if (trackHotels) {
        for (let i = 0; i < 10; i++) {
          const hotel =
            trackHotels.outward?.[i]?.hotel || trackHotels.return?.[i]?.hotel
          if (hotel) {
            const placeholder = `[Steckenhotel${i < 9 ? 'Hin' : 'Rück'}${
              i + 1
            }Name]`
            result = result.replace(placeholder, hotel.title || '')
          }
        }
      }

      // Teilnehmer Ausweisfreischaltung
      const headerTeilnehmerAusweisfreischaltung =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Anreise</th><th>Abreise</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerAusweisfreischaltung: string = ''
      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantDateFromChanges = participantChanges.find((pC) =>
            pC.change.path.includes('dateFrom')
          )
          const participantDateToChanges = participantChanges.find((pC) =>
            pC.change.path.includes('dateTo')
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          tableTeilnehmerAusweisfreischaltung =
            tableTeilnehmerAusweisfreischaltung.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${
              getParticipantTypeDisplayName(x, t)
              // x.unknownPerson
              //   ? t(`enum.personType.${x.unknownPerson.personType}`)
              //   : x && x.person && (x.person as User).external
              //   ? 'DVT/WVT'
              //   : t(`enum.personType.${x.person && x.person.type}`)
            }
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
        <td>
        ${x.dateFrom ? onFormatDate(new Date(x.dateFrom)) : ''}
        </td>
        <td>
        ${x.dateTo ? onFormatDate(new Date(x.dateTo)) : ''}
        </td>
        <td>${
          x._changes.changeType === 'REMOVE'
            ? 'Teilnehmer wurde storniert'
            : x._changes.changeType === 'ADD'
            ? 'Neuer Teilnehmer'
            : ''
        }
          ${
            participantDateToChanges &&
            participantDateToChanges.change &&
            participantDateToChanges.change.oldValue !== null &&
            !isDeleted &&
            !isAdded
              ? participantDateFromChanges &&
                participantDateFromChanges.change &&
                participantDateFromChanges.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                ? `Alter Wert: ` +
                  onFormatDate(
                    new Date(participantDateFromChanges.change.oldValue)
                  )
                : ''
              : participantDateFromChanges &&
                participantDateFromChanges.change &&
                participantDateFromChanges.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
              ? `Alter Wert Anreise: ` +
                onFormatDate(
                  new Date(participantDateFromChanges.change.oldValue)
                )
              : ''
          }${
                participantDateFromChanges &&
                participantDateFromChanges.change &&
                participantDateFromChanges.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantDateToChanges &&
                    participantDateToChanges.change &&
                    participantDateToChanges.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantDateToChanges.change.oldValue)
                      )
                    : ''
                  : participantDateToChanges &&
                    participantDateToChanges.change &&
                    participantDateToChanges.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Abreise:  ` +
                    onFormatDate(
                      new Date(participantDateToChanges.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerAusweisfreischaltung\]/g,
        `<table>${headerTeilnehmerAusweisfreischaltung}${tableTeilnehmerAusweisfreischaltung}</table>`
      )

      // Teilnehmer Flugbuchung
      const headerTeilnehmerFlugbuchung =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Hinflug</th><th>Rückflug</th><th>Airline</th><th>Flugnummer</th><th>Bemerkungen</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerFlugbuchung: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantFlightOutboundChanges = participantChanges.find(
            (pC) => pC.change.path.includes('flight.outboundData.departure')
          )
          const participantFlightReturnChanges = participantChanges.find((pC) =>
            pC.change.path.includes('flight.returnData.departure')
          )
          const participantDateFromChanges = participantChanges.find((pC) =>
            pC.change.path.includes('].dateFrom')
          )
          const participantDateToChanges = participantChanges.find((pC) =>
            pC.change.path.includes('].dateTo')
          )
          const participantFlightTypeChange = participantChanges.find(
            (pC) => pC.change.value === 'noFlight'
          )
          const hasReturnChanges =
            participantFlightReturnChanges &&
            participantFlightReturnChanges.change &&
            participantFlightReturnChanges.change.oldValue
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          tableTeilnehmerFlugbuchung = tableTeilnehmerFlugbuchung.concat(
            `<tr>
            <td>${
              x.person
                ? x.person.name
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.External)
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Worker
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier &&
                  x.unknownPerson.surName !== null
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier
                ? '[Teilnehmer noch unbekannt]'
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.External)
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : ''
            }</td><td>${
              x.person && x.person.department
                ? x.person.department
                : x.unknownPerson && x.unknownPerson.department
                ? x.unknownPerson.department
                : ''
            }</td><td>
            ${
              getParticipantTypeDisplayName(x, t)
              // x.unknownPerson
              //   ? t(`enum.personType.${x.unknownPerson.personType}`)
              //   : x && x.person && (x.person as User).external
              //   ? 'DVT/WVT'
              //   : t(`enum.personType.${x.person && x.person.type}`)
            }
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
        <td>${x.orderId ? x.orderId : ''}</td>
        <td>
        ${
          x.flight &&
          (x.flight.flightType === FlightType.OutboundFlight ||
            x.flight.flightType === FlightType.Roundtrip) &&
          x.flight.outboundData &&
          x.flight.outboundData.departure
            ? onFormatDate(new Date(x.flight.outboundData.departure))
            : x.flight &&
              (x.flight.flightType === FlightType.OutboundFlight ||
                x.flight.flightType === FlightType.Roundtrip)
            ? onFormatDate(new Date(x.dateFrom))
            : ''
        }
        </td>
        <td>
        ${
          // to show DATE of Return
          x.flight && // has flight data to be checked
          (x.flight.flightType === FlightType.ReturnFlight ||
            x.flight.flightType === FlightType.Roundtrip) && // check flight was booked
          x.flight.returnData && // has flight detail to be checked
          x.flight.returnData.departure // date of flight was booked
            ? onFormatDate(new Date(x.flight.returnData.departure))
            : x.flight &&
              (x.flight.flightType === FlightType.ReturnFlight ||
                x.flight.flightType === FlightType.Roundtrip)
            ? onFormatDate(new Date(x.dateTo))
            : ''
        }
        </td>
        <td>
        ${
          // to show Airline
          [
            x.flight &&
            x.flight.outboundData &&
            !['noFlight', 'returnFlight'].includes(x.flight?.flightType) &&
            x.flight.outboundData.airline !== null &&
            x.flight.outboundData.airline !== undefined
              ? x.flight.outboundData?.airline
              : '-',
            x.flight &&
            x.flight.returnData &&
            !['noFlight', 'outboundFlight'].includes(x.flight?.flightType) &&
            x.flight.returnData.airline !== null &&
            x.flight.returnData.airline !== undefined
              ? x.flight.returnData?.airline
              : '-',
          ].join(' / ')
        }
      </td>
      <td>
      ${
        // to show Flight Number
        [
          x.flight &&
          x.flight.outboundData &&
          !['noFlight', 'returnFlight'].includes(x.flight?.flightType) &&
          x.flight.outboundData.flightNumber !== null &&
          x.flight.outboundData.flightNumber !== undefined
            ? x.flight.outboundData?.flightNumber
            : '-',
          x.flight &&
          x.flight.returnData &&
          !['noFlight', 'outboundFlight'].includes(x.flight?.flightType) &&
          x.flight.returnData.flightNumber !== null &&
          x.flight.returnData.flightNumber !== undefined
            ? x.flight.returnData?.flightNumber
            : '-',
        ].join(' / ')
      }
    </td>
        <td>
        ${
          // to Show Note
          x.flight && x.flight.note && x.flight?.flightType !== 'noFlight'
            ? x.flight.note
            : ''
        }
        </td>
        <td>
        ${
          isDeleted
            ? 'Teilnehmer wurde storniert'
            : isAdded
            ? 'Neuer Teilnehmer'
            : participantFlightTypeChange
            ? '(Storniert) '
            : ' '
        }
        ${
          x.flight &&
          x.flight.outboundData &&
          x.flight.outboundData.departure &&
          participantFlightOutboundChanges &&
          !hasReturnChanges &&
          !isDeleted &&
          !isAdded
            ? `Alter Wert Hinflug: ` +
              onFormatDate(
                new Date(participantFlightOutboundChanges.change.oldValue)
              )
            : x.dateFrom &&
              participantDateFromChanges &&
              !participantDateToChanges &&
              !hasReturnChanges &&
              !isDeleted &&
              !isAdded
            ? `Alter Wert Hinflug: ` +
              onFormatDate(new Date(participantDateFromChanges.change.oldValue))
            : ''
        }${
              (participantFlightReturnChanges || participantDateToChanges) &&
              (participantFlightOutboundChanges ||
                participantDateFromChanges) &&
              !isDeleted &&
              !isAdded
                ? `Alter Wert: ` +
                  onFormatDate(
                    new Date(
                      participantFlightOutboundChanges
                        ? participantFlightOutboundChanges.change.oldValue
                        : participantDateFromChanges
                        ? participantDateFromChanges.change.oldValue
                        : ''
                    )
                  ) +
                  ` - ` +
                  onFormatDate(
                    new Date(
                      participantFlightReturnChanges
                        ? participantFlightReturnChanges.change.oldValue
                        : participantDateToChanges
                        ? participantDateToChanges.change.oldValue
                        : ''
                    )
                  )
                : participantFlightReturnChanges ||
                  (participantDateToChanges && !isDeleted && !isAdded)
                ? `Alter Wert Rückflug: ` +
                  onFormatDate(
                    new Date(
                      participantFlightReturnChanges
                        ? participantFlightReturnChanges.change.oldValue
                        : participantDateToChanges
                        ? participantDateToChanges.change.oldValue
                        : ''
                    )
                  )
                : ''
            }</td>
       </tr>`
          )
        })
      result = result.replace(
        /\[TeilnehmerFlugbuchung\]/g,
        `<table>${headerTeilnehmerFlugbuchung}${tableTeilnehmerFlugbuchung}</table>`
      )

      // Teilnehmer Fähre
      const headerTeilnehmerFähre =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Strecke</th><th>Auftragsnummer</th><th>Fahrzeug</th><th>Maße</th><th>Kennzeichen</th><th>Abfahrt</th><th>Uhrzeit</th><th>Anmerkungen</th></tr>'

      let tableTeilnehmerFähreHinfahrt: string = ''
      let tableTeilnehmerFähreRückfahrt: string = ''
      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantFerryOutboundChanges = participantChanges.find(
            (pC) => pC.change.path.includes('ferry.dateFrom')
          )
          const participantFerryOutward = participantChanges.find(
            (pC) =>
              pC.change.path.includes('ferry.outward') &&
              pC.change.value === null
          )

          tableTeilnehmerFähreHinfahrt = tableTeilnehmerFähreHinfahrt.concat(
            `<tr><td>${
              x.person
                ? x.person.name
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.External)
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Worker
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier &&
                  x.unknownPerson.surName !== null
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier
                ? '[Teilnehmer noch unbekannt]'
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.External)
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : ''
            }</td><td>${
              x.person && x.person.department
                ? x.person.department
                : x.unknownPerson && x.unknownPerson.department
                ? x.unknownPerson.department
                : ''
            }</td><td>
            ${
              getParticipantTypeDisplayName(x, t)
              // x.unknownPerson
              //   ? t(`enum.personType.${x.unknownPerson.personType}`)
              //   : x && x.person && (x.person as User).external
              //   ? 'DVT/WVT'
              //   : t(`enum.personType.${x.person && x.person.type}`)
            }
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
        <td>${
          x.ferry && x.ferry.outward && x.ferry.outward.title
            ? x.ferry.outward.title
            : ''
        }</td>
        <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      </td>
    <td></td>
    <td></td>
    <td>
    ${
      x.ferry && x.ferry.dateFrom
        ? onFormatDate(new Date(x.ferry.dateFrom))
        : ''
    }</td>
    <td>${x.ferry && x.ferry.timeFrom ? x.ferry.timeFrom : ''}</td>
      <td>
      ${
        x._changes.changeType === 'REMOVE'
          ? 'Teilnehmer wurde storniert'
          : x._changes.changeType === 'ADD'
          ? 'Neuer Teilnehmer'
          : participantFerryOutward
          ? '(Storniert) '
          : ''
      }
      ${
        participantFerryOutboundChanges &&
        participantFerryOutboundChanges.change &&
        participantFerryOutboundChanges.change.oldValue !== null
          ? `Alter Wert: ` +
            onFormatDate(
              new Date(participantFerryOutboundChanges.change.oldValue)
            )
          : ''
      }</td></tr>`
          )
        })

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantFerryReturnChanges = participantChanges.find((pC) =>
            pC.change.path.includes('ferry.dateTo')
          )
          const participantFerryReturn = participantChanges.find(
            (pC) =>
              pC.change.path.includes('ferry.return') &&
              pC.change.value === null
          )

          tableTeilnehmerFähreRückfahrt = tableTeilnehmerFähreRückfahrt.concat(
            `<tr><td>${
              x.person
                ? x.person.name
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.External)
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Worker
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier &&
                  x.unknownPerson.surName !== null
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier
                ? '[Teilnehmer noch unbekannt]'
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.External)
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : ''
            }</td><td>${
              x.person && x.person.department
                ? x.person.department
                : x.unknownPerson && x.unknownPerson.department
                ? x.unknownPerson.department
                : ''
            }</td><td>
            ${
              getParticipantTypeDisplayName(x, t)
              // x.unknownPerson
              //   ? t(`enum.personType.${x.unknownPerson.personType}`)
              //   : x && x.person && (x.person as User).external
              //   ? 'DVT/WVT'
              //   : t(`enum.personType.${x.person && x.person.type}`)
            }
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
        <td>${
          x.ferry && x.ferry.return && x.ferry.return.title
            ? x.ferry.return.title
            : ''
        }</td>
        <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      </td>
    <td></td>
    <td></td>
    <td>${
      x.ferry && x.ferry.dateTo ? onFormatDate(new Date(x.ferry.dateTo)) : ''
    }</td>
    <td>${x.ferry && x.ferry.timeTo ? x.ferry.timeTo : ''}</td>
      <td>
      ${
        x._changes.changeType === 'REMOVE'
          ? 'Teilnehmer wurde storniert'
          : x._changes.changeType === 'ADD'
          ? 'Neuer Teilnehmer'
          : participantFerryReturn
          ? '(Storniert) '
          : ''
      }
      ${
        participantFerryReturnChanges &&
        participantFerryReturnChanges.change &&
        participantFerryReturnChanges.change.oldValue !== null
          ? `Alter Wert:  ` +
            onFormatDate(
              new Date(participantFerryReturnChanges.change.oldValue)
            )
          : ''
      }</td></tr>`
          )
        })

      result = result.replace(
        /\[TeilnehmerFähre\]/g,
        `Hinfahrt:<br/><table>${headerTeilnehmerFähre}${tableTeilnehmerFähreHinfahrt}</table><br/>Rückfahrt:<br/><table>${headerTeilnehmerFähre}${tableTeilnehmerFähreRückfahrt}</table>`
      )

      // Teilnehmer Mietwagen
      const headerTeilnehmerMietwagen =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Kategorie</th><th>Abholung</th><th>Rückgabe</th><th>Bemerkungen</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerMietwagen: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantRentalCarDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes('rentalCar.dateFrom')
          )
          const participantRentalCarDateTo = participantChanges.find((pC) =>
            pC.change.path.includes('rentalCar.dateTo')
          )
          const participantRentalCar = participantChanges.find(
            (pC) =>
              pC.change.path.includes('rentalCar.carType') &&
              pC.change.value === 'none'
          )

          tableTeilnehmerMietwagen = tableTeilnehmerMietwagen.concat(
            `<tr><td>${
              x.person
                ? x.person.name
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.External)
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Worker
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier &&
                  x.unknownPerson.surName !== null
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier
                ? '[Teilnehmer noch unbekannt]'
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.External)
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : ''
            }</td><td>${
              x.person && x.person.department
                ? x.person.department
                : x.unknownPerson && x.unknownPerson.department
                ? x.unknownPerson.department
                : ''
            }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
        <td>${x.orderId ? x.orderId : ''}</td>
        <td>${
          x.rentalCar && x.rentalCar.carType
            ? t(`enum.rentalCar.${x.rentalCar.carType}`)
            : ''
        }</td>
        <td>
        ${
          x.rentalCar && x.rentalCar.dateFrom
            ? onFormatDate(new Date(x.rentalCar.dateFrom))
            : ''
        }
        </td>
        <td>
        ${
          x.rentalCar && x.rentalCar.dateTo
            ? onFormatDate(new Date(x.rentalCar.dateTo))
            : ''
        }
        </td>
        <td>
        ${
          // to Show Note
          x.rentalCar && x.rentalCar.note ? x.rentalCar.note : ''
        }
        </td>
        <td>
        ${
          x._changes.changeType === 'REMOVE'
            ? 'Teilnehmer wurde storniert'
            : x._changes.changeType === 'ADD'
            ? 'Neuer Teilnehmer'
            : participantRentalCar
            ? '(Storniert) '
            : ''
        }${
              participantRentalCarDateTo &&
              participantRentalCarDateTo.change &&
              participantRentalCarDateTo.change.oldValue !== null
                ? participantRentalCarDateFrom &&
                  participantRentalCarDateFrom.change &&
                  participantRentalCarDateFrom.change.oldValue !== null
                  ? `Alter Wert: ` +
                    onFormatDate(
                      new Date(participantRentalCarDateFrom.change.oldValue)
                    )
                  : ''
                : participantRentalCarDateFrom &&
                  participantRentalCarDateFrom.change &&
                  participantRentalCarDateFrom.change.oldValue !== null
                ? `Alter Wert Abholung: ` +
                  onFormatDate(
                    new Date(participantRentalCarDateFrom.change.oldValue)
                  )
                : ''
            }${
              participantRentalCarDateFrom &&
              participantRentalCarDateFrom.change &&
              participantRentalCarDateFrom.change.oldValue !== null
                ? participantRentalCarDateTo &&
                  participantRentalCarDateTo.change &&
                  participantRentalCarDateTo.change.oldValue !== null
                  ? ` - ` +
                    onFormatDate(
                      new Date(participantRentalCarDateTo.change.oldValue)
                    )
                  : ''
                : participantRentalCarDateTo &&
                  participantRentalCarDateTo.change &&
                  participantRentalCarDateTo.change.oldValue !== null
                ? `Alter Wert Rückgabe:  ` +
                  onFormatDate(
                    new Date(participantRentalCarDateTo.change.oldValue)
                  )
                : ''
            }</td></tr>`
          )
        })

      result = result.replace(
        /\[TeilnehmerMietwagen\]/g,
        `<table>${headerTeilnehmerMietwagen}${tableTeilnehmerMietwagen}</table>`
      )

      // Teilnehmer Unterkunft
      const headerTeilnehmerUnterkunft =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerUnterkunft: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantAccomodationDateFrom = participantChanges.find(
            (pC) => pC.change.path.includes('].accomodation.dateFrom')
          )
          const participantAccomodationDateTo = participantChanges.find((pC) =>
            pC.change.path.includes('].accomodation.dateTo')
          )
          const participantAccomodation = participantChanges.find(
            (pC) =>
              pC.change.path.endsWith('].accomodation.accomodation') &&
              pC.change.value === null
          )

          const deleted = x._changes.changes.find(
            (f: any) =>
              f.change.relativePath === 'accomodation.accomodation' &&
              f.change.type === 'REMOVE'
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'
          tableTeilnehmerUnterkunft = tableTeilnehmerUnterkunft.concat(
            `<tr><td>${
              x.person
                ? x.person.name
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.External)
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Worker
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier &&
                  x.unknownPerson.surName !== null
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier
                ? '[Teilnehmer noch unbekannt]'
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.External)
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : ''
            }</td><td>${
              x.person && x.person.department
                ? x.person.department
                : x.unknownPerson && x.unknownPerson.department
                ? x.unknownPerson.department
                : ''
            }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
        <td>${x.orderId ? x.orderId : ''}</td>
        <td>
        ${
          x.accomodation && x.accomodation.dateFrom
            ? onFormatDate(new Date(x.accomodation.dateFrom))
            : ''
        }
        </td>
        <td>
        ${
          x.accomodation && x.accomodation.dateTo
            ? onFormatDate(new Date(x.accomodation.dateTo))
            : ''
        }
        </td>
        <td>
        ${
          isDeleted
            ? 'Teilnehmer wurde storniert'
            : deleted
            ? '(Storniert)'
            : isAdded
            ? 'Neuer Teilnehmer'
            : isUpdated &&
              participantAccomodationDateTo &&
              participantAccomodationDateTo.change &&
              !participantAccomodationDateTo.change.oldValue &&
              participantAccomodationDateFrom &&
              participantAccomodationDateFrom.change &&
              !participantAccomodationDateFrom.change.oldValue
            ? 'Neu'
            : participantAccomodation
            ? '(Storniert) '
            : ''
        }
${
  participantAccomodationDateTo &&
  participantAccomodationDateTo.change &&
  participantAccomodationDateTo.change.oldValue &&
  participantAccomodationDateFrom &&
  participantAccomodationDateFrom.change &&
  participantAccomodationDateFrom.change.oldValue &&
  !isDeleted &&
  !isAdded
    ? `Alter Wert: ` +
      onFormatDate(new Date(participantAccomodationDateFrom.change.oldValue)) +
      ` - ` +
      onFormatDate(new Date(participantAccomodationDateTo.change.oldValue))
    : participantAccomodationDateTo &&
      participantAccomodationDateTo.change.oldValue &&
      !(
        participantAccomodationDateFrom &&
        participantAccomodationDateFrom.change.oldValue
      ) &&
      !isDeleted &&
      !isAdded
    ? `Alter Wert Check-Out: ` +
      onFormatDate(new Date(participantAccomodationDateTo.change.oldValue))
    : participantAccomodationDateFrom &&
      participantAccomodationDateFrom.change.oldValue &&
      !(
        participantAccomodationDateTo &&
        participantAccomodationDateTo.change.oldValue
      ) &&
      !isDeleted &&
      !isAdded
    ? `Alter Wert Check-In: ` +
      onFormatDate(new Date(participantAccomodationDateFrom.change.oldValue))
    : ''
}
        </td></tr>`
          )
        })

      result = result.replace(
        /\[TeilnehmerUnterkunft\]/g,
        `<table>${headerTeilnehmerUnterkunft}${tableTeilnehmerUnterkunft}</table>`
      )

      // Teilnehmer Streckenhotel
      const headerTeilnehmerStreckenhotelHin1 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin1: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[0]
                  ? x.trackHotels.outward[0]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[0]
                  ? x.trackHotels.outward[0]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'
          tableTeilnehmerStreckenhotelHin1 =
            tableTeilnehmerStreckenhotelHin1.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[0] &&
        x.trackHotels.outward[0].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[0].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[0] &&
        x.trackHotels.outward[0].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[0].dateTo))
          : ''
      }
      </td>
      <td>
      ${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }
      ${
        participantTrackHotelDateTo &&
        participantTrackHotelDateTo.change &&
        participantTrackHotelDateTo.change.oldValue !== null &&
        !isDeleted &&
        !isAdded
          ? participantTrackHotelDateFrom &&
            participantTrackHotelDateFrom.change &&
            participantTrackHotelDateFrom.change.oldValue !== null &&
            !isDeleted &&
            !isAdded
            ? `Alter Wert: ` +
              onFormatDate(
                new Date(participantTrackHotelDateFrom.change.oldValue)
              )
            : ''
          : participantTrackHotelDateFrom &&
            participantTrackHotelDateFrom.change &&
            participantTrackHotelDateFrom.change.oldValue !== null &&
            !isDeleted &&
            !isAdded
          ? `Alter Wert Check-In: ` +
            onFormatDate(
              new Date(participantTrackHotelDateFrom.change.oldValue)
            )
          : ''
      }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin1\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin1}${tableTeilnehmerStreckenhotelHin1}</table>`
      )

      const headerTeilnehmerStreckenhotelHin2 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin2: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[1]
                  ? x.trackHotels.outward[1]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[1]
                  ? x.trackHotels.outward[1]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelHin2 =
            tableTeilnehmerStreckenhotelHin2.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[1] &&
        x.trackHotels.outward[1].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[1].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[1] &&
        x.trackHotels.outward[1].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[1].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin2\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin2}${tableTeilnehmerStreckenhotelHin2}</table>`
      )

      const headerTeilnehmerStreckenhotelHin3 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin3: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[2]
                  ? x.trackHotels.outward[2]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[2]
                  ? x.trackHotels.outward[2]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelHin3 =
            tableTeilnehmerStreckenhotelHin3.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[2] &&
        x.trackHotels.outward[2].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[2].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[2] &&
        x.trackHotels.outward[2].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[2].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin3\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin3}${tableTeilnehmerStreckenhotelHin3}</table>`
      )

      const headerTeilnehmerStreckenhotelHin4 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin4: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[3]
                  ? x.trackHotels.outward[3]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[3]
                  ? x.trackHotels.outward[3]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelHin4 =
            tableTeilnehmerStreckenhotelHin4.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[3] &&
        x.trackHotels.outward[3].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[3].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[3] &&
        x.trackHotels.outward[3].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[3].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin4\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin4}${tableTeilnehmerStreckenhotelHin4}</table>`
      )

      const headerTeilnehmerStreckenhotelHin5 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin5: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[4]
                  ? x.trackHotels.outward[4]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[4]
                  ? x.trackHotels.outward[4]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelHin5 =
            tableTeilnehmerStreckenhotelHin5.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[4] &&
        x.trackHotels.outward[4].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[4].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[4] &&
        x.trackHotels.outward[4].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[4].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin5\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin5}${tableTeilnehmerStreckenhotelHin5}</table>`
      )

      const headerTeilnehmerStreckenhotelHin6 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin6: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[5]
                  ? x.trackHotels.outward[5]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[5]
                  ? x.trackHotels.outward[5]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelHin6 =
            tableTeilnehmerStreckenhotelHin6.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[5] &&
        x.trackHotels.outward[5].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[5].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[5] &&
        x.trackHotels.outward[5].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[5].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin6\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin6}${tableTeilnehmerStreckenhotelHin6}</table>`
      )

      const headerTeilnehmerStreckenhotelHin7 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin7: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[6]
                  ? x.trackHotels.outward[6]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[6]
                  ? x.trackHotels.outward[6]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelHin7 =
            tableTeilnehmerStreckenhotelHin7.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[6] &&
        x.trackHotels.outward[6].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[6].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[6] &&
        x.trackHotels.outward[6].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[6].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin7\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin7}${tableTeilnehmerStreckenhotelHin7}</table>`
      )

      const headerTeilnehmerStreckenhotelHin8 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin8: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[7]
                  ? x.trackHotels.outward[7]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[7]
                  ? x.trackHotels.outward[7]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelHin8 =
            tableTeilnehmerStreckenhotelHin8.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[7] &&
        x.trackHotels.outward[7].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[7].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[7] &&
        x.trackHotels.outward[7].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[7].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin8\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin8}${tableTeilnehmerStreckenhotelHin8}</table>`
      )

      const headerTeilnehmerStreckenhotelHin9 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin9: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[8]
                  ? x.trackHotels.outward[8]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[8]
                  ? x.trackHotels.outward[8]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelHin9 =
            tableTeilnehmerStreckenhotelHin9.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[8] &&
        x.trackHotels.outward[8].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[8].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[8] &&
        x.trackHotels.outward[8].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[8].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin9\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin9}${tableTeilnehmerStreckenhotelHin9}</table>`
      )

      const headerTeilnehmerStreckenhotelHin10 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelHin10: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[9]
                  ? x.trackHotels.outward[9]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.outward\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.outward[9]
                  ? x.trackHotels.outward[9]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelHin10 =
            tableTeilnehmerStreckenhotelHin10.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[9] &&
        x.trackHotels.outward[9].dateFrom
          ? onFormatDate(new Date(x.trackHotels.outward[9].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.outward[9] &&
        x.trackHotels.outward[9].dateTo
          ? onFormatDate(new Date(x.trackHotels.outward[9].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelHin10\]/g,
        `<table>${headerTeilnehmerStreckenhotelHin10}${tableTeilnehmerStreckenhotelHin10}</table>`
      )

      // Teilnehmer Streckenhotel Rück
      const headerTeilnehmerStreckenhotelRück1 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück1: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[0]
                  ? x.trackHotels.return[0]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[0]
                  ? x.trackHotels.return[0]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelRück1 =
            tableTeilnehmerStreckenhotelRück1.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[0] &&
        x.trackHotels.return[0].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[0].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[0] &&
        x.trackHotels.return[0].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[0].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück1\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück1}${tableTeilnehmerStreckenhotelRück1}</table>`
      )

      const headerTeilnehmerStreckenhotelRück2 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück2: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[1]
                  ? x.trackHotels.return[1]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[1]
                  ? x.trackHotels.return[1]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'
          tableTeilnehmerStreckenhotelRück2 =
            tableTeilnehmerStreckenhotelRück2.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[1] &&
        x.trackHotels.return[1].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[1].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[1] &&
        x.trackHotels.return[1].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[1].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück2\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück2}${tableTeilnehmerStreckenhotelRück2}</table>`
      )

      const headerTeilnehmerStreckenhotelRück3 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück3: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[2]
                  ? x.trackHotels.return[2]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[2]
                  ? x.trackHotels.return[2]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelRück3 =
            tableTeilnehmerStreckenhotelRück3.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[2] &&
        x.trackHotels.return[2].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[2].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[2] &&
        x.trackHotels.return[2].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[2].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück3\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück3}${tableTeilnehmerStreckenhotelRück3}</table>`
      )

      const headerTeilnehmerStreckenhotelRück4 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück4: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[3]
                  ? x.trackHotels.return[3]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[3]
                  ? x.trackHotels.return[3]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelRück4 =
            tableTeilnehmerStreckenhotelRück4.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[3] &&
        x.trackHotels.return[3].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[3].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[3] &&
        x.trackHotels.return[3].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[3].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück4\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück4}${tableTeilnehmerStreckenhotelRück4}</table>`
      )

      const headerTeilnehmerStreckenhotelRück5 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück5: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[4]
                  ? x.trackHotels.return[4]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[4]
                  ? x.trackHotels.return[4]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelRück5 =
            tableTeilnehmerStreckenhotelRück5.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[4] &&
        x.trackHotels.return[4].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[4].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[4] &&
        x.trackHotels.return[4].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[4].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück5\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück5}${tableTeilnehmerStreckenhotelRück5}</table>`
      )

      const headerTeilnehmerStreckenhotelRück6 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück6: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[5]
                  ? x.trackHotels.return[5]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[5]
                  ? x.trackHotels.return[5]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelRück6 =
            tableTeilnehmerStreckenhotelRück6.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[5] &&
        x.trackHotels.return[5].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[5].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[5] &&
        x.trackHotels.return[5].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[5].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück6\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück6}${tableTeilnehmerStreckenhotelRück6}</table>`
      )

      const headerTeilnehmerStreckenhotelRück7 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück7: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[6]
                  ? x.trackHotels.return[6]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[6]
                  ? x.trackHotels.return[6]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelRück7 =
            tableTeilnehmerStreckenhotelRück7.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[6] &&
        x.trackHotels.return[6].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[6].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[6] &&
        x.trackHotels.return[6].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[6].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück7\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück7}${tableTeilnehmerStreckenhotelRück7}</table>`
      )

      const headerTeilnehmerStreckenhotelRück8 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück8: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[7]
                  ? x.trackHotels.return[7]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[7]
                  ? x.trackHotels.return[7]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelRück8 =
            tableTeilnehmerStreckenhotelRück8.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[7] &&
        x.trackHotels.return[7].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[7].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[7] &&
        x.trackHotels.return[7].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[7].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück8\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück8}${tableTeilnehmerStreckenhotelRück8}</table>`
      )

      const headerTeilnehmerStreckenhotelRück9 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück9: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[8]
                  ? x.trackHotels.return[8]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[8]
                  ? x.trackHotels.return[8]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelRück9 =
            tableTeilnehmerStreckenhotelRück9.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[8] &&
        x.trackHotels.return[8].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[8].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[8] &&
        x.trackHotels.return[8].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[8].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück9\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück9}${tableTeilnehmerStreckenhotelRück9}</table>`
      )

      const headerTeilnehmerStreckenhotelRück10 =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Check-In</th><th>Check-Out</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerStreckenhotelRück10: string = ''

      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          const participantChanges = changes.filter((c) =>
            c.change.path.includes(x._id)
          )
          const participantTrackHotelDateFrom = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[9]
                  ? x.trackHotels.return[9]._id
                  : ''
              }\'\)\].dateFrom`
            )
          )
          const participantTrackHotelDateTo = participantChanges.find((pC) =>
            pC.change.path.includes(
              `trackHotels.return\[?\(\@\.\_id\=\'${
                x.trackHotels && x.trackHotels.return[9]
                  ? x.trackHotels.return[9]._id
                  : ''
              }\'\)\].dateTo`
            )
          )
          const isDeleted = x._changes.changeType === 'REMOVE'
          const isAdded = x._changes.changeType === 'ADD'
          const isUpdated = x._changes.changeType === 'UPDATE'

          tableTeilnehmerStreckenhotelRück10 =
            tableTeilnehmerStreckenhotelRück10.concat(
              `<tr><td>${
                x.person
                  ? x.person.name
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === true &&
                      x.unknownPerson.personType === PersonType.External)
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Worker
                  ? '[Teilnehmer noch unbekannt]'
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier &&
                    x.unknownPerson.surName !== null
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.Supplier
                  ? '[Teilnehmer noch unbekannt]'
                  : (x.unknownPerson &&
                      x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.User) ||
                    (x.unknownPerson.isUnknown === false &&
                      x.unknownPerson.personType === PersonType.External)
                  ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                  : ''
              }</td><td>${
                x.person && x.person.department
                  ? x.person.department
                  : x.unknownPerson && x.unknownPerson.department
                  ? x.unknownPerson.department
                  : ''
              }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
      <td>${x.orderId ? x.orderId : ''}</td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[9] &&
        x.trackHotels.return[9].dateFrom
          ? onFormatDate(new Date(x.trackHotels.return[9].dateFrom))
          : ''
      }
      </td>
      <td>
      ${
        x.trackHotels &&
        x.trackHotels.return[9] &&
        x.trackHotels.return[9].dateTo
          ? onFormatDate(new Date(x.trackHotels.return[9].dateTo))
          : ''
      }
      </td>
      <td>${
        isDeleted
          ? 'Teilnehmer wurde storniert'
          : isAdded
          ? 'Neuer Teilnehmer'
          : isUpdated &&
            !participantTrackHotelDateFrom &&
            !participantTrackHotelDateTo
          ? 'Neu'
          : ''
      }${
                participantTrackHotelDateTo &&
                participantTrackHotelDateTo.change &&
                participantTrackHotelDateTo.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? `Alter Wert: ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateFrom.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateFrom &&
                    participantTrackHotelDateFrom.change &&
                    participantTrackHotelDateFrom.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-In: ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateFrom.change.oldValue)
                    )
                  : ''
              }${
                participantTrackHotelDateFrom &&
                participantTrackHotelDateFrom.change &&
                participantTrackHotelDateFrom.change.oldValue !== null &&
                !isDeleted &&
                !isAdded
                  ? participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                    ? ` - ` +
                      onFormatDate(
                        new Date(participantTrackHotelDateTo.change.oldValue)
                      )
                    : ''
                  : participantTrackHotelDateTo &&
                    participantTrackHotelDateTo.change &&
                    participantTrackHotelDateTo.change.oldValue !== null &&
                    !isDeleted &&
                    !isAdded
                  ? `Alter Wert Check-Out:  ` +
                    onFormatDate(
                      new Date(participantTrackHotelDateTo.change.oldValue)
                    )
                  : ''
              }</td></tr>`
            )
        })

      result = result.replace(
        /\[TeilnehmerStreckenhotelRück10\]/g,
        `<table>${headerTeilnehmerStreckenhotelRück10}${tableTeilnehmerStreckenhotelRück10}</table>`
      )

      // Teilnehmer Freie Mail
      const headerTeilnehmerFreieMail =
        '<tr><th>Name</th><th>Abteilung</th><th>Art</th><th>Personalnummer</th><th>Auftragsnummer</th><th>Anreise</th><th>Abreise</th><th>Anmerkung</th></tr>'

      let tableTeilnehmerFreieMail: string = ''
      // tslint:disable-next-line: no-unused-expression
      context &&
        context.forEach((x: any) => {
          tableTeilnehmerFreieMail = tableTeilnehmerFreieMail.concat(
            `<tr><td>${
              x.person
                ? x.person.name
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === true &&
                    x.unknownPerson.personType === PersonType.External)
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Worker
                ? '[Teilnehmer noch unbekannt]'
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier &&
                  x.unknownPerson.surName !== null
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : x.unknownPerson.isUnknown === true &&
                  x.unknownPerson.personType === PersonType.Supplier
                ? '[Teilnehmer noch unbekannt]'
                : (x.unknownPerson &&
                    x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.User) ||
                  (x.unknownPerson.isUnknown === false &&
                    x.unknownPerson.personType === PersonType.External)
                ? `${x.unknownPerson.firstName} ${x.unknownPerson.surName}`
                : ''
            }</td><td>${
              x.person && x.person.department
                ? x.person.department
                : x.unknownPerson && x.unknownPerson.department
                ? x.unknownPerson.department
                : ''
            }</td><td>
            ${getParticipantTypeDisplayName(x, t)}
            </td><td>${
              x.person && x.person.employeeId
                ? x.person.employeeId
                : x.person && x.person._id && x.person._id.length < 24
                ? x.person._id
                : ''
            }</td>
          <td>
          ${x.orderId ? x.orderId : ''}
          </td>
        <td>
        ${x.dateFrom ? onFormatDate(new Date(x.dateFrom)) : ''}
        </td>
        <td>
        ${x.dateTo ? onFormatDate(new Date(x.dateTo)) : ''}
        </td>
        <td></td></tr>`
          )
        })

      result = result.replace(
        /\[TeilnehmerFreieMail\]/g,
        `<table>${headerTeilnehmerFreieMail}${tableTeilnehmerFreieMail}</table>`
      )

      // Teilnehmer Steuer

      // Teilnehmer Signatur
      result = result.replace(
        /\[Footer\]/g,
        auth && auth.user && auth.user.signature ? auth.user.signature : ''
      )

      return result
    },
    [auth, changes, context, proving, t]
  )

  const replaceEMailVariable = useCallback(
    (contentToReplace: any): any => {
      let result: string[] = contentToReplace

      result = result.map((x) =>
        x.replace(
          /\[EMailReisebüro\]/g,
          context &&
            context[0] &&
            context[0].accomodation &&
            context[0].accomodation.accomodation &&
            context[0].accomodation.accomodation.emailHRG
            ? context[0].accomodation.accomodation.emailHRG
            : ''
        )
      )

      result = result.map((x) =>
        x.replace(
          /\[EMailHotel\]/g,
          context &&
            context[0] &&
            context[0].accomodation &&
            context[0].accomodation.accomodation &&
            context[0].accomodation.accomodation.email
            ? context[0].accomodation.accomodation.email
            : ''
        )
      )

      result = result.map((x) =>
        x.replace(
          /\[AntragstellerMail\]/g,
          proving &&
            proving.createdBy &&
            proving.createdBy &&
            proving.createdBy.email
            ? proving.createdBy.email
            : ''
        )
      )
      if (result.includes('[VerantwortlicherMail]')) {
        // tslint:disable-next-line: no-unused-expression
        proving && proving.responsible.forEach((r) => result.push(r.email!))
      }
      result = result.map((x) => x.replace(/\[VerantwortlicherMail\]/g, ''))

      if (result.includes('[StellvertreterMail]')) {
        // tslint:disable-next-line: no-unused-expression
        proving &&
          proving.deputy &&
          proving.deputy.forEach((r) => result.push(r.email!))
      }
      result = result.map((x) => x.replace(/\[StellvertreterMail\]/g, ''))

      return result
    },
    [context, proving]
  )

  const handleRemove = (file: any, index: number, fieldProps: any) => {
    fieldProps.form.values.attachments.splice(index, 1)
    deleteBlob({
      variables: {
        uri: file,
      },
    })
  }

  React.useEffect(() => {
    if (!initialValues) {
      if (mode === MailboxFormMode.New) {
        if (proving) {
          if (templateId) {
            generateMailFromTemplate({
              variables: {
                provingId: proving._id,
                templateId,
                context,
              },
            }).then((result) => {
              setinitialValues(
                result.data && result.data.generateMailFromTemplate
                  ? {
                      ...result.data.generateMailFromTemplate,
                      to: replaceEMailVariable(
                        result.data.generateMailFromTemplate.to
                      ),
                      cc: replaceEMailVariable(
                        result.data.generateMailFromTemplate.cc
                      ),
                      provingTag: {
                        key: proving._id,
                        name: proving.id,
                      } as ITag,
                    }
                  : initialMail || emptyMail
              )
            })
          } else {
            setinitialValues({
              ...(initialMail || emptyMail),
              provingTag: { key: proving._id, name: proving.id } as ITag,
            })
          }
        } else {
          setinitialValues(initialMail || emptyMail)
        }
      } else if (mode === MailboxFormMode.Reply) {
        if (mailData && mailData.mail) {
          const mail = mailData.mail
          setinitialValues({
            to: mail.from && mail.from.address ? [mail.from.address] : [],
            subject: `AW: ${mail.subject}`,
            body: replaceVariables(`\[Footer\]<br /><br /><hr />
          ${mail.body}`),
            provingTag: proving
              ? ({ key: proving._id, name: proving.id } as ITag)
              : null,
            attachments: mail.attachments,
          })
        }
      } else {
        if (mailsData && mailsData.mails && mailsData.mails.length === 1) {
          const mail = mailsData.mails[0]
          setinitialValues({
            to: mail.to ? mail.to.map((x) => x.address) : [],
            cc: mail.cc ? mail.cc.map((x) => x.address) : [],
            subject: replaceVariables(mail.subject),
            body: replaceVariables(mail.body),
            provingTag: proving
              ? ({ key: proving._id, name: proving.id } as ITag)
              : null,
            attachments: mail.attachments,
          })
        }
      }
    }
  }, [
    proving,
    templateId,
    mailsData,
    context,
    generateMailFromTemplate,
    mode,
    initialMail,
    replaceVariables,
    initialValues,
    replaceEMailVariable,
  ])

  const onFileUploadComplete = (
    files: Partial<Blob>[],
    fieldProps: FieldProps<any>
  ) => {
    let currentFieldValue = fieldProps.field.value
    if (!isArray(currentFieldValue)) {
      currentFieldValue = []
    }
    for (const file of files) {
      if (file.containerUri && file.name && file.uri) {
        currentFieldValue.push({ name: file.name, uri: file.uri })
      }
    }

    fieldProps.form.setFieldValue(fieldProps.field.name, currentFieldValue)
  }

  React.useEffect(() => {
    if (loadNewMails && mode !== MailboxFormMode.Reply) {
      loadMails({
        variables: {
          filter: { _id: { EQ: mailId } },
          provingId: proving ? proving._id : null,
        },
      })
    } else if (mode === MailboxFormMode.Reply && mailId) {
      loadMail({
        variables: {
          _id: mailId,
        },
      })
    }
  }, [mailId, loadMails, proving])

  const MailboxFormComponent: React.FC = () => {
    const currentFormikContext = useCurrentFormik()

    useEffect(() => {
      currentFormikContext.validateForm(currentFormikContext.values)
    }, [])
    return (
      <FlexSpinner loading={isSubmitting}>
        <Text variant={'large'}>
          {mode === MailboxFormMode.New ? t('Neue E-Mail') : t('E-Mail öffnen')}
        </Text>
        <Stack tokens={themedMediumStackTokens}>
          {mode === MailboxFormMode.Edit && (
            <Stack horizontal={true}>
              <Stack.Item>
                <Label style={{ width: '70px' }}>
                  {t('field.mailTemplate.from')}
                </Label>
              </Stack.Item>
              <Stack.Item grow={1}>
                <Field
                  name="from"
                  render={(formikProps: FieldProps<Mail>) => (
                    <FormikMailAddressPicker
                      {...formikProps}
                      underline={true}
                      disabled={true}
                    />
                  )}
                />
              </Stack.Item>
            </Stack>
          )}
          <Stack horizontal={true}>
            <Stack.Item>
              <Label required={true} style={{ width: '70px' }}>
                {t('field.mailTemplate.to')}
              </Label>
            </Stack.Item>
            <Stack.Item grow={1}>
              <Field
                name="to"
                render={(formikProps: FieldProps<Mail>) => (
                  <FormikMailAddressPicker
                    {...formikProps}
                    underline={true}
                    disabled={mode === MailboxFormMode.Edit}
                  />
                )}
              />
            </Stack.Item>
          </Stack>
          <Stack horizontal={true}>
            <Stack.Item>
              <Label style={{ width: '70px' }}>
                {t('field.mailTemplate.cc')}
              </Label>
            </Stack.Item>
            <Stack.Item grow={1}>
              <Field
                name="cc"
                render={(formikProps: FieldProps<Mail>) => (
                  <FormikMailAddressPicker
                    {...formikProps}
                    underline={true}
                    disabled={mode === MailboxFormMode.Edit}
                  />
                )}
              />
            </Stack.Item>
          </Stack>
          <Stack horizontal={true}>
            <Stack.Item>
              <Label required={true} style={{ width: '70px' }}>
                {t('field.mailTemplate.subject')}
              </Label>
            </Stack.Item>
            <Stack.Item grow={1}>
              <Field
                name="subject"
                render={(fieldProps: FieldProps<Mail>) => (
                  <TextField
                    underlined={true}
                    {...mapFieldToTextField(fieldProps)}
                    value={replaceVariables(fieldProps.field.value)}
                    disabled={mode === MailboxFormMode.Edit}
                  />
                )}
              />
            </Stack.Item>
          </Stack>
          <Stack horizontal={true}>
            <Stack.Item>
              <Label style={{ width: '70px' }}>
                {t('field.mailTemplate.proving')}
              </Label>
            </Stack.Item>
            <Stack.Item grow={1}>
              <Field
                name="provingTag"
                render={(fieldProps: FieldProps<Mail>) => (
                  <TagPicker
                    itemLimit={1}
                    styles={{
                      text: { borderWidth: 0, borderBottomWidth: 1 },
                    }}
                    defaultSelectedItems={
                      fieldProps.field.value ? [fieldProps.field.value] : []
                    }
                    onResolveSuggestions={(filter) => {
                      return (provings || [])
                        .filter((x) => x.id.includes(filter.toUpperCase()))
                        .map((y) => {
                          return { key: y._id, name: y.id } as ITag
                        })
                    }}
                    onChange={(items) => {
                      fieldProps.form.setFieldValue(
                        fieldProps.field.name,
                        items && items.length === 1 ? items[0] : null
                      )
                    }}
                  />
                )}
              />
            </Stack.Item>
          </Stack>
          {(mode === MailboxFormMode.New ||
            mode === MailboxFormMode.EditDraft ||
            mode === MailboxFormMode.Reply) && (
            <FieldStackItem
              name="attachments"
              render={(fieldProps) => (
                <Stack tokens={{ childrenGap: 5 }}>
                  <FileUpload
                    parentId={proving ? proving._id : uniqueId('newMails')}
                    parentType="mailAttachment"
                    noDragEventsBubbling={true}
                    onUploadCompleted={(files) =>
                      onFileUploadComplete(files, fieldProps)
                    }
                  />
                  {fieldProps.field.value &&
                    Array.isArray(fieldProps.field.value) &&
                    fieldProps.field.value.map((x, i) => {
                      return (
                        <span key={x.uri}>
                          <Icon iconName="attach" />
                          <DownloadLink uri={x.uri}>{x.name}</DownloadLink>
                          <IconButton
                            styles={{
                              root: {
                                color: 'red',
                                marginLeft: 5,
                                marginTop: 4,
                                width: 10,
                                height: 10,
                              },
                            }}
                            iconProps={{ iconName: 'Delete' }}
                            onClick={() => handleRemove(x.uri, i, fieldProps)}
                          />
                        </span>
                      )
                    })}
                </Stack>
              )}
            />
          )}
        </Stack>
        {mode === MailboxFormMode.Edit && (
          <Stack tokens={themedMediumStackTokens}>
            <Field
              name="attachments"
              render={(fieldProps: FieldProps<Mail>) => (
                <Stack tokens={{ childrenGap: 5 }} horizontal={true}>
                  {fieldProps.field.value &&
                    Array.isArray(fieldProps.field.value) &&
                    fieldProps.field.value.map((x) => {
                      return (
                        <span key={x.uri}>
                          <Icon iconName="attach" />
                          <DownloadLink uri={x.uri}>{x.name}</DownloadLink>
                        </span>
                      )
                    })}
                </Stack>
              )}
            />
          </Stack>
        )}
        <Stack tokens={themedMediumStackTokens}>
          <Field
            name="body"
            render={(fieldProps: FieldProps<Mail>) =>
              mode === MailboxFormMode.New ||
              mode === MailboxFormMode.EditDraft ||
              mode === MailboxFormMode.Reply ? (
                // wait till the body is ready, because the editor value can only be set during the first render
                fieldProps.field.value !== undefined ? (
                  <TextEditor
                    css={{ fontSize: 22 }}
                    value={replaceVariables(fieldProps.field.value)}
                    onChange={(value) => {
                      fieldProps.form.setFieldValue('body', value || '')
                    }}
                  />
                ) : (
                  <></>
                )
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: fieldProps.field.value,
                  }}
                />
              )
            }
          />
          {mode !== MailboxFormMode.Edit && canSaveAsDraft && (
            <Field
              name="draftEnabled"
              render={(fieldProps: FieldProps<Mail>) => (
                <Checkbox
                  defaultChecked={mode === MailboxFormMode.EditDraft}
                  label={t('Als Entwurf speichern')}
                  onChange={(_, isChecked) => {
                    setIsDraft(!!isChecked)
                    fieldProps.form.setFieldValue('draftEnabled', isChecked)
                  }}
                />
              )}
            />
          )}
        </Stack>
      </FlexSpinner>
    )
  }

  if (!initialValues || loadingMails) {
    return <></>
  }

  return (
    <FormikPanel
      isOpen={isOpen}
      enableSubmit={
        mode === MailboxFormMode.EditDraft ||
        (location === 'participants' &&
          initialValues.to.length > 0 &&
          initialValues.subject.length > 0)
      }
      initialValues={initialValues}
      onDismiss={(ev) => {
        onDismiss()
      }}
      type={PanelType.large}
      submitText={!!isDraft ? 'Entwurf speichern' : 'Versenden'}
      validationSchema={new MailFormValidationSchema()}
      onSubmit={async (values, formikActions) => {
        formikActions.setSubmitting(true)
        setIsSubmitting(true)
        if (mode === MailboxFormMode.New || mode === MailboxFormMode.Reply) {
          // removeNullProperties({ attachments: values.attachments })
          createMail({
            variables: {
              provingId: values.provingTag ? values.provingTag.key : null,
              input: {
                // if draft, save to draft folder
                folder: isDraft ? MailFolder.Draft : MailFolder.Outbox,
                to: values.to.map((x: any) => {
                  return {
                    address: x,
                  }
                }),
                cc: values.cc
                  ? values.cc.map((x: any) => {
                      return {
                        address: x,
                      }
                    })
                  : [],
                subject: replaceVariables(values.subject),
                body: replaceVariables(values.body),
              },
              attachments: values.attachments
                ? omitDeep(values.attachments, '__typename')
                : [],
            },
          })
        } else if (mode === MailboxFormMode.Edit) {
          await updateMail({
            variables: {
              id: mailId!,
              update: {
                proving: values.provingTag ? values.provingTag.key : null,
              },
            },
          })
        } else if (mode === MailboxFormMode.EditDraft) {
          await updateMail({
            variables: {
              id: mailId!,
              update: {
                proving: values.provingTag ? values.provingTag.key : null,
                folder: isDraft ? MailFolder.Draft : MailFolder.Outbox,
                to: values.to.map((x: any) => {
                  return {
                    address: x,
                  }
                }),
                cc: values.cc
                  ? values.cc.map((x: any) => {
                      return {
                        address: x,
                      }
                    })
                  : [],
                subject: replaceVariables(values.subject),
                body: replaceVariables(values.body),
              },
            },
          })
        }
        formikActions.setSubmitting(false)
        if (refetchMail) {
          refetchMail()
        }
        if (onSaved) {
          onSaved()
        }
        setIsSubmitting(false)
      }}
    >
      <MailboxFormComponent />
    </FormikPanel>
  )
}
